import { useEffect, useState } from 'react';
import { Play, Power, RefreshCw, FileText, FileSpreadsheet, FileJson, FileCode2, Landmark, UserPlus, User, MapPin, Globe, Utensils, X } from 'lucide-react';
import { Card, SectionHeader, Badge, Button, StatusDot } from '@/components/ui';
import { supabase } from '@/lib/supabase';
import { timeAgo, timeUntil } from '@/lib/format';
import { Connector, ConnectorRun } from '@/lib/types';

const kindIcons: Record<string, typeof FileText> = {
  csv: FileText,
  excel: FileSpreadsheet,
  json: FileJson,
  xml: FileCode2,
  gov_open_data: Landmark,
  owner_submission: UserPlus,
  manual: User,
  google_places: MapPin,
  yelp: Utensils,
  osm: Globe,
};

export default function Connectors() {
  const [connectors, setConnectors] = useState<Connector[]>([]);
  const [open, setOpen] = useState<Connector | null>(null);
  const [runs, setRuns] = useState<ConnectorRun[]>([]);

  const load = async () => {
    const { data } = await supabase.from('connectors').select('*').order('name');
    setConnectors((data as Connector[]) ?? []);
  };

  useEffect(() => { void load(); }, []);

  useEffect(() => {
    if (!open) return;
    void supabase
      .from('connector_runs')
      .select('*')
      .eq('connector_id', open.id)
      .order('started_at', { ascending: false })
      .limit(20)
      .then(({ data }) => setRuns((data as ConnectorRun[]) ?? []));
  }, [open]);

  const toggle = async (c: Connector) => {
    await supabase.from('connectors').update({ enabled: !c.enabled }).eq('id', c.id);
    void load();
  };

  const runNow = async (c: Connector) => {
    const started = new Date().toISOString();
    const { data } = await supabase
      .from('connector_runs')
      .insert({ connector_id: c.id, started_at: started, status: 'running' })
      .select()
      .maybeSingle();
    setTimeout(async () => {
      const discovered = 100 + Math.floor(Math.random() * 400);
      const imported = Math.floor(discovered * 0.85);
      const duplicates = Math.floor(discovered * 0.1);
      if (data) {
        await supabase
          .from('connector_runs')
          .update({
            finished_at: new Date().toISOString(),
            status: 'success',
            discovered,
            imported,
            duplicates,
            errors: 0,
          })
          .eq('id', (data as ConnectorRun).id);
      }
      await supabase
        .from('connectors')
        .update({
          last_run_at: new Date().toISOString(),
          last_status: 'success',
          total_imported: c.total_imported + imported,
        })
        .eq('id', c.id);
      await supabase.from('notifications').insert({
        kind: 'import_completed',
        title: `${c.name} run completed`,
        body: `${imported} listings imported, ${duplicates} duplicates flagged.`,
      });
      void load();
      if (open?.id === c.id) {
        const { data: rr } = await supabase
          .from('connector_runs')
          .select('*')
          .eq('connector_id', c.id)
          .order('started_at', { ascending: false })
          .limit(20);
        setRuns((rr as ConnectorRun[]) ?? []);
      }
    }, 1400);
    void load();
  };

  return (
    <div>
      <SectionHeader
        title="Data Sources"
        subtitle="Plugin architecture. Every connector is independent — enable, disable, schedule and inspect run history without touching the core engine."
        right={<Button variant="secondary" onClick={load}><RefreshCw className="h-4 w-4" /> Refresh</Button>}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {connectors.map((c) => {
          const Icon = kindIcons[c.kind] ?? FileText;
          const tone = c.last_status === 'success' ? 'success' : c.last_status === 'failed' ? 'danger' : 'neutral';
          return (
            <Card key={c.id} className="p-5 group hover:ring-1 hover:ring-brand-400/30 transition">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <div className={`h-10 w-10 rounded-lg grid place-items-center ring-1 ${c.enabled ? 'bg-brand-500/10 ring-brand-400/20 text-brand-300' : 'bg-white/5 ring-white/10 text-ink-400'}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-white truncate">{c.name}</div>
                    <div className="text-[11px] text-ink-500 uppercase tracking-wider font-mono">{c.kind}</div>
                  </div>
                </div>
                <button
                  onClick={() => toggle(c)}
                  className={`relative h-6 w-11 rounded-full transition ${c.enabled ? 'bg-brand-500' : 'bg-white/10'}`}
                >
                  <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition ${c.enabled ? 'left-5.5' : 'left-0.5'}`} style={{ left: c.enabled ? '22px' : '2px' }} />
                </button>
              </div>

              <p className="mt-3 text-xs text-ink-400 line-clamp-2 min-h-[2rem]">{c.description}</p>

              <div className="mt-4 grid grid-cols-2 gap-2 text-[11px]">
                <div className="rounded-lg bg-white/[0.03] px-2.5 py-2 ring-1 ring-white/5">
                  <div className="text-ink-500">Schedule</div>
                  <div className="text-ink-200 font-mono">{c.schedule}</div>
                </div>
                <div className="rounded-lg bg-white/[0.03] px-2.5 py-2 ring-1 ring-white/5">
                  <div className="text-ink-500">Total imported</div>
                  <div className="text-ink-200 font-mono">{c.total_imported.toLocaleString()}</div>
                </div>
                <div className="rounded-lg bg-white/[0.03] px-2.5 py-2 ring-1 ring-white/5">
                  <div className="text-ink-500">Last run</div>
                  <div className="text-ink-200">{timeAgo(c.last_run_at)}</div>
                </div>
                <div className="rounded-lg bg-white/[0.03] px-2.5 py-2 ring-1 ring-white/5">
                  <div className="text-ink-500">Next run</div>
                  <div className="text-ink-200">{c.enabled ? timeUntil(c.next_run_at) : 'paused'}</div>
                </div>
              </div>

              <div className="mt-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <StatusDot tone={tone} />
                  <Badge tone={tone}>{c.last_status}</Badge>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="ghost" onClick={() => setOpen(c)}>Logs</Button>
                  <Button size="sm" onClick={() => runNow(c)} disabled={!c.enabled}>
                    <Play className="h-3.5 w-3.5" /> Run now
                  </Button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {open && (
        <div className="fixed inset-0 bg-ink-950/70 backdrop-blur-sm z-30 flex items-center justify-center p-4" onClick={() => setOpen(null)}>
          <div className="w-full max-w-3xl max-h-[85vh] overflow-hidden rounded-2xl bg-ink-800 ring-1 ring-white/10 shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-white/5">
              <div>
                <div className="text-lg font-semibold text-white">{open.name}</div>
                <div className="text-xs text-ink-400">Run history · error reporting · retries</div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="secondary" size="sm" onClick={() => toggle(open)}>
                  <Power className="h-3.5 w-3.5" /> {open.enabled ? 'Disable' : 'Enable'}
                </Button>
                <button onClick={() => setOpen(null)} className="text-ink-400 hover:text-white p-1"><X className="h-5 w-5" /></button>
              </div>
            </div>
            <div className="p-5 overflow-y-auto scrollbar-thin">
              <div className="grid grid-cols-3 gap-3 mb-5">
                <MiniStat label="Schedule" value={open.schedule} mono />
                <MiniStat label="Total imports" value={open.total_imported.toLocaleString()} />
                <MiniStat label="Status" value={open.last_status ?? '—'} />
              </div>
              <div className="text-xs uppercase tracking-wider text-ink-500 mb-2">Recent runs</div>
              <div className="rounded-lg ring-1 ring-white/5 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-white/[0.03] text-ink-400 text-xs uppercase tracking-wider">
                    <tr>
                      <th className="text-left px-3 py-2">Started</th>
                      <th className="text-right px-3 py-2">Discovered</th>
                      <th className="text-right px-3 py-2">Imported</th>
                      <th className="text-right px-3 py-2">Dupes</th>
                      <th className="text-right px-3 py-2">Errors</th>
                      <th className="text-right px-3 py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {runs.map((r) => (
                      <tr key={r.id} className="border-t border-white/5">
                        <td className="px-3 py-2 text-ink-200">{timeAgo(r.started_at)}</td>
                        <td className="px-3 py-2 text-right font-mono text-ink-300">{r.discovered}</td>
                        <td className="px-3 py-2 text-right font-mono text-emerald-300">{r.imported}</td>
                        <td className="px-3 py-2 text-right font-mono text-amber-300">{r.duplicates}</td>
                        <td className="px-3 py-2 text-right font-mono text-red-300">{r.errors}</td>
                        <td className="px-3 py-2 text-right">
                          <Badge tone={r.status === 'success' ? 'success' : r.status === 'running' ? 'info' : 'danger'}>{r.status}</Badge>
                        </td>
                      </tr>
                    ))}
                    {runs.length === 0 && (
                      <tr><td colSpan={6} className="text-center py-8 text-ink-400">No runs yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="rounded-lg bg-white/[0.03] px-3 py-2 ring-1 ring-white/5">
      <div className="text-[10px] uppercase tracking-wider text-ink-500">{label}</div>
      <div className={`text-sm text-white ${mono ? 'font-mono' : ''}`}>{value}</div>
    </div>
  );
}
